import React, { useEffect, useRef, useState } from 'react';
import { Box, Collapse, IconButton, Stack, TableRow, Typography } from '@mui/material';
import MyTableCell from '../../../../components/material-ui/adaptive/MyTableCell';
import CusLabelSelect from '../../../../components/material-ui/CusLabelSelect';
import { IconCopy, IconX } from '@tabler/icons-react';
import useColor from '../../../../hooks/site/useColor';
import CusInput from '../../../../components/material-ui/CusInput';
import BtnSave from '../../../../components/bonus/ui/BtnSave';
import BtnTrash from '../../../../components/bonus/ui/BtnTrash';
import useToast from '../../../../hooks/useToast';
import useBootstrapSize from '../../../../hooks/useBootstrapSize';
import ShowContainer from '../../../../components/material-ui/ShowContainer';

// Достаёт значение параметра из строки "a=1&b=2"
const getParam = (params, key) => {
  const part = (params || '').split('&').find((p) => p.startsWith(`${key}=`));
  return part ? part.slice(key.length + 1) : '';
};

// Заменяет (или добавляет) параметр; пустое значение удаляет параметр
const setParam = (params, key, value) => {
  const parts = (params || '').split('&').filter((p) => p && !p.startsWith(`${key}=`));
  if (value) parts.push(`${key}=${value}`);
  return parts.join('&');
};

export default function CurrentReferral({
  refLinksInfo,
  link,
  selectRow,
  setSelectRow,
  deleteReferralHandler,
  onCommentChange,
  selectGame,
  selectMode
}) {
  const { info } = useColor();
  const { infoToast } = useToast();
  const { isDesktop } = useBootstrapSize();

  const commentRef = useRef(null);

  const domains = refLinksInfo?.domainList || [];
  const botName = refLinksInfo?.botName;
  const linkId = link.id;
  const isSelect = selectRow === link.id;

  const domainsOptions = domains.map((el) => ({ value: el.name, label: el.name }));
  const pbParams = link.pbParams || '';
  const comment = link.comment;

  const [domain, setDomain] = useState(domains.find((el) => el.default)?.name);
  const [tempComment, setTempComment] = useState();
  // Параметры, которые видит пользователь (без game)
  const [tempStatParam, setTempStatParam] = useState(setParam(pbParams, 'game', ''));
  // game хранится отдельно и пользователю в поле не показывается
  const [tempGame, setTempGame] = useState(getParam(pbParams, 'game'));
  const [saveDisable, setSaveDisable] = useState(false);

  useEffect(() => {
    setDomain(domains.find((el) => el.default)?.name);
  }, [domains.length]);

  // 1. Подтягиваем сохранённые параметры
  useEffect(() => {
    setTempStatParam(setParam(pbParams, 'game', ''));
    setTempGame(getParam(pbParams, 'game'));
  }, [pbParams]);

  // 2. Поверх них применяем выбранные режим и игру (только для выбранной строки)
  useEffect(() => {
    if (!isSelect) return;
    if (selectMode) setTempStatParam((prev) => setParam(prev, 'type', selectMode));
    if (selectGame) setTempGame(String(selectGame));
  }, [isSelect, selectMode, selectGame]);

  useEffect(() => {
    setTempComment(comment);
  }, [comment]);

  const onCopyClick = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        infoToast('copied to your clipboard');
      })
      .catch((err) => {
        console.error('Ошибка копирования: ', err);
      });
  };

  const onRemoveGame = (e) => {
    e.stopPropagation();
    setTempGame('');
    setTempStatParam((prev) => setParam(prev, 'type', ''));
  };

  const onDelete = (id) => {
    deleteReferralHandler(id);
  };

  const onSaveComment = async (e) => {
    e.stopPropagation();
    setSaveDisable(true);
    // game добавляем обратно в параметры только при сохранении
    const paramsToSave = setParam(tempStatParam, 'game', tempGame);
    await onCommentChange(linkId, tempComment, paramsToSave);
    setSaveDisable(false);
  };

  const textLinkOrig = `?start=r_${link.refcode}`;
  // Для бота: без gamePath и без type
  const tgParams = setParam(tempStatParam, 'type', '');
  const gamePath = tempGame ? `/game/${tempGame}` : '';

  let tgLink = 'https://t.me/' + botName + textLinkOrig;
  if (tgParams) tgLink += '&' + tgParams;
  tgLink = tgLink.replaceAll('&', '__');

  let copySiteLink = 'https://' + domain + gamePath + textLinkOrig;
  if (tempStatParam) copySiteLink += '&' + tempStatParam;
  if (link.anchor) copySiteLink += link.anchor;

  return (
    <TableRow
      key={link.id}
      bgcolor={isSelect && info.light}
      onClick={() => {
        setSelectRow((prev) => (prev === link.id ? undefined : link.id));
      }}
      sx={{ cursor: 'pointer' }}>
      <MyTableCell>
        <Stack display={'flex'} direction={'column'} gap={0.5}>
          <Stack display={'flex'} direction={'row'} justifyContent={'space-between'}>
            <Stack display={'flex'} direction={'column'} gap={0.5}>
              <ShowContainer condition={domain}>
                <Stack display={'flex'} flexDirection={'row'} alignItems={'center'}>
                  https://
                  <CusLabelSelect
                    options={domainsOptions}
                    value={domain}
                    onChange={(val) => {
                      setDomain(val);
                    }}
                  />
                  <Stack direction={'row'} alignItems={'center'}>
                    <ShowContainer condition={tempGame}>
                      <Stack
                        sx={{ position: 'relative', top: '-8px' }}
                        gap={0}
                        alignItems={'center'}>
                        <IconX
                          size={14}
                          onClick={onRemoveGame}
                          style={{ cursor: 'pointer', top: 0, right: 0 }}
                        />
                        <Typography p={0} whiteSpace={'nowrap'}>
                          {gamePath}
                        </Typography>
                      </Stack>
                    </ShowContainer>
                    <Typography p={0} whiteSpace={'nowrap'}>
                      {textLinkOrig}
                    </Typography>
                  </Stack>
                  &
                  <CusInput
                    value={tempStatParam}
                    placeholder={'Static parameters'}
                    width={isDesktop ? 210 : undefined}
                    onChange={(e) => setTempStatParam(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    inputSx={{ padding: '4px' }}
                  />
                  {link.anchor}
                  <Stack marginRight={'auto'} display={'flex'} flexDirection={'row'} gap={1} pl={1}>
                    <IconCopy
                      size={16}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onCopyClick(copySiteLink);
                      }}
                    />
                  </Stack>
                </Stack>
              </ShowContainer>

              <ShowContainer condition={botName}>
                <Stack display={'flex'} direction={'row'} gap={1}>
                  {tgLink}
                  <Stack marginRight={'auto'} display={'flex'} flexDirection={'row'} gap={1} px={1}>
                    <IconCopy
                      size={16}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onCopyClick(tgLink);
                      }}
                    />
                  </Stack>
                </Stack>
              </ShowContainer>
            </Stack>
            <Stack display={'flex'} gap={1} direction={'column'}>
              <BtnSave onClick={onSaveComment} disabled={saveDisable} />
              <BtnTrash onDelete={() => onDelete(linkId)} />
            </Stack>
          </Stack>

          {/* <Collapse> */}
          <Stack display={'flex'} direction={'row'} gap={1}>
            <CusInput
              label={'Comment (not visible to the player)'}
              width={'100%'}
              ref={commentRef}
              value={tempComment}
              onChange={(e) => setTempComment(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              rows={2}
            />
          </Stack>
          {/* </Collapse> */}
        </Stack>
      </MyTableCell>
    </TableRow>
  );
}
